import { AnyAction } from "@reduxjs/toolkit";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import {
  portalRequestsApi,
  useGetPortalRequestQuery,
  useRemovePortalRequestFileMutation,
  useSubmitPortalRequestMutation,
} from "../../../slices/ClientRequestsSlice";
import { portalSlice } from "../../../slices/PortalSlice";
import { RequestItem } from "../../clientRequests/clientRequests.types";
import {
  answersPayload,
  isItemDone,
  MAX_ANSWER,
  progressOf,
  wantsFile,
} from "../../clientRequests/clientRequests.utils";
import {
  FileBadge,
  FirmBadge,
  PortalAlert,
  portalLink,
} from "../components/PortalUi";
import {
  errorMessage,
  fileProblem,
  isSignedOutError,
  PORTAL_ACCEPT,
} from "../portal.utils";
import { getPortalSession, setPortalSession } from "../portalSession";
import { uploadRequestFile } from "../requestUpload";

const card =
  "flex flex-col gap-3 rounded-[14px] border border-[#E8E8EC] bg-white p-[18px] sm:p-6";

/** The client completes a request list: upload files, answer questions, Yes / No, "Send to my accountant". */
export const PortalRequest = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const session = getPortalSession();
  const view = useGetPortalRequestQuery(id, { skip: !session || !id });
  const [submit, submitState] = useSubmitPortalRequestMutation();
  const [removeFile] = useRemovePortalRequestFileMutation();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [choices, setChoices] = useState<Record<string, boolean | null>>({});
  const [uploading, setUploading] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const signOut = () => {
    setPortalSession(null);
    dispatch(portalSlice.util.resetApiState());
    navigate("/portal", {
      replace: true,
      state: { notice: "Please sign in again." },
    });
  };

  useEffect(() => {
    if (isSignedOutError(view.error)) signOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.error]);

  if (!session) return <Navigate to={"/portal"} replace={true} />;

  const r = view.data;
  const items = r?.items ?? [];
  const choiceOf = (item: RequestItem) =>
    choices[item.id] !== undefined ? choices[item.id] : item.yesNo ?? null;
  const answerOf = (item: RequestItem) =>
    answers[item.id] !== undefined ? answers[item.id] : item.answer ?? "";
  const doneNow = items.filter((i) =>
    isItemDone(i, answerOf(i), choiceOf(i)),
  ).length;
  const p = progressOf({ doneCount: doneNow, totalCount: items.length });
  const pending = r ? answersPayload(items, answers, choices) : [];
  const open = r?.status === "sent";

  const replaceView = (next: typeof r) => {
    if (!next) return;
    dispatch(
      portalRequestsApi.util.updateQueryData(
        "getPortalRequest",
        id,
        () => next,
      ) as unknown as AnyAction,
    );
  };

  const upload = async (item: RequestItem, files: FileList | null) => {
    if (!files?.length) return;
    setError(null);
    setSaved(null);
    for (const file of Array.from(files)) {
      const problem = fileProblem(file);
      if (problem) {
        setError(problem);
        continue;
      }
      setUploading((u) => ({ ...u, [item.id]: 0 }));
      try {
        const next = await uploadRequestFile(id, item.id, file, (pct) =>
          setUploading((u) => ({ ...u, [item.id]: pct })),
        );
        replaceView(next);
      } catch (e) {
        if ((e as { status?: number }).status === 401) return signOut();
        setError((e as Error).message);
      } finally {
        setUploading((u) => {
          const copy = { ...u };
          delete copy[item.id];
          return copy;
        });
      }
    }
    dispatch(portalSlice.util.invalidateTags(["PortalFiles"]));
  };

  const send = async () => {
    setError(null);
    const tooLong = pending.find((a) => (a.answer || "").length > MAX_ANSWER);
    if (tooLong)
      return setError(`Answers can be at most ${MAX_ANSWER} characters.`);
    const result = await submit({ id, answers: pending });
    if ("error" in result) return setError(errorMessage(result.error));
    setAnswers({});
    setChoices({});
    setSaved(
      result.data.status === "complete"
        ? "Thank you! Everything was sent to your accountant."
        : "Saved and sent to your accountant. You can come back to finish the rest.",
    );
  };

  return (
    <div
      className={
        "flex min-h-screen flex-col bg-[#F1F2F5] font-[Inter,sans-serif] text-[#1F2433]"
      }>
      <header
        className={
          "flex items-center gap-3 border-b border-[#E8E8EC] bg-white px-4 py-3"
        }>
        <FirmBadge name={r?.firmName || "ProCharted"} size={"sm"} />
        <span className={"flex-1 text-sm font-extrabold"}>
          {r?.firmName || "Client portal"}
        </span>
        <Link to={"/portal/home"} className={`${portalLink} text-[13px]`}>
          My portal
        </Link>
      </header>

      <main
        className={
          "mx-auto flex w-full max-w-[720px] flex-col gap-4 px-4 py-6 pb-28"
        }>
        {view.isLoading && (
          <span className={"text-sm font-semibold text-[#6B7185]"}>
            Loading…
          </span>
        )}
        {view.error && !isSignedOutError(view.error) && (
          <div className={card}>
            <PortalAlert>{errorMessage(view.error)}</PortalAlert>
            <Link to={"/portal/home"} className={portalLink}>
              Back to my portal
            </Link>
          </div>
        )}

        {r && (
          <>
            <section className={card}>
              <span className={"text-[12.5px] font-bold text-[#6B7185]"}>
                Requests from {r.firmName} · {r.clientName}
              </span>
              <h1
                className={"m-0 text-[22px] font-extrabold tracking-[-0.3px]"}>
                {r.title}
              </h1>
              {r.dueDate && (
                <span className={"text-[13px] font-semibold text-[#3A4256]"}>
                  Please finish by{" "}
                  {moment(r.dueDate, "YYYY-MM-DD").format("MMMM D, YYYY")}
                </span>
              )}
              {r.message && (
                <p
                  className={
                    "m-0 whitespace-pre-wrap text-[14px] leading-relaxed text-[#3A4256]"
                  }>
                  {r.message}
                </p>
              )}
              <div className={"flex items-center gap-3"}>
                <div
                  className={
                    "h-2.5 flex-1 overflow-hidden rounded-full bg-[#EDEFF3]"
                  }
                  role={"progressbar"}
                  aria-valuenow={p.percent}
                  aria-valuemin={0}
                  aria-valuemax={100}>
                  <div
                    className={
                      "h-full rounded-full bg-[#6B4EFF] transition-all"
                    }
                    style={{ width: `${p.percent}%` }}
                  />
                </div>
                <span className={"text-[13px] font-bold"}>{p.label} done</span>
              </div>
              {r.status === "complete" && (
                <span
                  className={
                    "rounded-[10px] bg-[#E6F6EE] px-3 py-2 text-[13px] font-semibold text-[#12805C]"
                  }>
                  ✓ Complete. {r.firmName} has everything they asked for.
                </span>
              )}
            </section>

            <ol className={"m-0 flex list-none flex-col gap-3 p-0"}>
              {items.map((item, index) => {
                const done = isItemDone(item, answerOf(item), choiceOf(item));
                const yes = choiceOf(item);
                const showUpload = wantsFile({ ...item, yesNo: yes });
                const pct = uploading[item.id];
                return (
                  <li key={item.id} className={card}>
                    <div className={"flex items-start gap-3"}>
                      <span
                        aria-label={done ? "Done" : "To do"}
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-extrabold ${
                          done
                            ? "bg-[#12805C] text-white"
                            : "border-2 border-[#DCE2EA] text-[#9097A8]"
                        }`}>
                        {done ? "✓" : index + 1}
                      </span>
                      <span
                        className={"flex-1 text-[15px] font-bold leading-snug"}>
                        {item.label}
                      </span>
                    </div>

                    {item.kind === "question" && (
                      <textarea
                        className={
                          "min-h-[72px] w-full rounded-[9px] border-[1.5px] border-[#DCE2EA] px-3 py-2 text-[14px] outline-none focus:border-[#6B4EFF] disabled:bg-[#F7F8FA]"
                        }
                        aria-label={item.label}
                        placeholder={"Type your answer"}
                        maxLength={MAX_ANSWER}
                        disabled={!open}
                        value={answerOf(item)}
                        onChange={(e) =>
                          setAnswers((a) => ({
                            ...a,
                            [item.id]: e.target.value,
                          }))
                        }
                      />
                    )}

                    {item.kind === "yesno" && (
                      <div className={"flex gap-2"} role={"radiogroup"}>
                        {[true, false].map((v) => (
                          <button
                            key={String(v)}
                            type={"button"}
                            role={"radio"}
                            aria-checked={yes === v}
                            disabled={!open}
                            onClick={() =>
                              setChoices((c) => ({ ...c, [item.id]: v }))
                            }
                            className={`h-10 min-w-[84px] rounded-[9px] border-[1.5px] px-4 text-[14px] font-bold ${
                              yes === v
                                ? "border-[#6B4EFF] bg-[#F4F2FE] text-[#4B2FC7]"
                                : "border-[#DCE2EA] bg-white text-[#3A4256] hover:bg-[#F7F8FA]"
                            }`}>
                            {v ? "Yes" : "No"}
                          </button>
                        ))}
                      </div>
                    )}

                    {item.files.length > 0 && (
                      <ul className={"m-0 flex list-none flex-col gap-1.5 p-0"}>
                        {item.files.map((f) => (
                          <li
                            key={f.documentId}
                            className={
                              "flex items-center gap-2 rounded-[10px] border border-[#EDEFF3] px-3 py-2"
                            }>
                            <FileBadge name={f.name} />
                            <span
                              className={
                                "min-w-0 flex-1 truncate text-[13.5px] font-semibold"
                              }>
                              {f.name}
                            </span>
                            {f.canDelete && open && (
                              <button
                                type={"button"}
                                className={`${portalLink} text-[12.5px]`}
                                onClick={async () => {
                                  setError(null);
                                  const res = await removeFile({
                                    id,
                                    itemId: item.id,
                                    documentId: f.documentId,
                                  });
                                  if ("error" in res)
                                    setError(errorMessage(res.error));
                                }}>
                                Remove
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {showUpload && open && (
                      <div className={"flex flex-wrap items-center gap-2"}>
                        <input
                          ref={(el) => (fileInputs.current[item.id] = el)}
                          type={"file"}
                          multiple
                          accept={PORTAL_ACCEPT}
                          className={"hidden"}
                          onChange={(e) => {
                            upload(item, e.target.files);
                            e.target.value = "";
                          }}
                        />
                        <button
                          type={"button"}
                          disabled={pct !== undefined}
                          onClick={() => fileInputs.current[item.id]?.click()}
                          className={
                            "h-10 rounded-[9px] border-[1.5px] border-[#6B4EFF] bg-white px-4 text-[13.5px] font-extrabold text-[#6B4EFF] hover:bg-[#F7F5FF] disabled:opacity-60"
                          }>
                          {pct !== undefined
                            ? `Uploading… ${pct}%`
                            : item.files.length
                            ? "Upload another file"
                            : "Upload"}
                        </button>
                        <span
                          className={"text-[12px] font-medium text-[#6B7185]"}>
                          PDF, photos, Word or Excel · up to 50 MB
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>

            {error && <PortalAlert>{error}</PortalAlert>}
            {saved && (
              <div
                role={"status"}
                className={
                  "rounded-[10px] bg-[#E6F6EE] px-3 py-2 text-[13.5px] font-semibold text-[#12805C]"
                }>
                {saved}
              </div>
            )}
          </>
        )}
      </main>

      {r && open && (
        <div
          className={
            "fixed inset-x-0 bottom-0 border-t border-[#E8E8EC] bg-white px-4 py-3"
          }>
          <div
            className={
              "mx-auto flex max-w-[720px] flex-wrap items-center justify-between gap-3"
            }>
            <span className={"text-[13px] font-semibold text-[#6B7185]"}>
              Files are sent as soon as they upload. Press the button to send
              your answers.
            </span>
            <button
              type={"button"}
              onClick={send}
              disabled={submitState.isLoading}
              className={
                "h-11 rounded-[9px] bg-[#6B4EFF] px-5 text-[14px] font-extrabold text-white shadow-[0_3px_10px_rgba(107,78,255,0.3)] hover:bg-[#5B3DF5] disabled:opacity-60"
              }>
              {submitState.isLoading ? "Sending…" : "Send to my accountant"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
